import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { AdminDashboardDTO, DoctorDashboardDTO, OfficerDashboardDTO, CoordinatorDashboardDTO } from '../../types/dashboard';
import api from '../../services/api';

/**
 * Dashboard Slice
 * Redux state management for dashboard data.
 *
 * @author Civilhealth Development Team
 */

// State interface
interface DashboardState {
  adminDashboard: AdminDashboardDTO | null;
  doctorDashboard: DoctorDashboardDTO | null;
  officerDashboard: OfficerDashboardDTO | null;
  coordinatorDashboard: CoordinatorDashboardDTO | null;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: DashboardState = {
  adminDashboard: null,
  doctorDashboard: null,
  officerDashboard: null,
  coordinatorDashboard: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchAdminDashboard = createAsyncThunk(
  'dashboard/fetchAdminDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/admin');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch admin dashboard');
    }
  }
);

export const fetchDoctorDashboard = createAsyncThunk(
  'dashboard/fetchDoctorDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/doctor');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch doctor dashboard');
    }
  }
);

export const fetchOfficerDashboard = createAsyncThunk(
  'dashboard/fetchOfficerDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/officer');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch officer dashboard');
    }
  }
);

// Coordinator dashboard (Phase-2 pending approvals)
export const fetchCoordinatorDashboard = createAsyncThunk(
  'dashboard/fetchCoordinatorDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/dashboard/coordinator');
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch coordinator dashboard');
    }
  }
);

// Slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearDashboardError: (state) => {
      state.error = null;
    },
    clearAdminDashboard: (state) => {
      state.adminDashboard = null;
    },
    clearDoctorDashboard: (state) => {
      state.doctorDashboard = null;
    },
    clearOfficerDashboard: (state) => {
      state.officerDashboard = null;
    },
    clearCoordinatorDashboard: (state) => {
      state.coordinatorDashboard = null;
    },
  },
  extraReducers: (builder) => {
    // Admin Dashboard
    builder
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.adminDashboard = action.payload;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Doctor Dashboard
    builder
      .addCase(fetchDoctorDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.doctorDashboard = action.payload;
      })
      .addCase(fetchDoctorDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Officer Dashboard
    builder
      .addCase(fetchOfficerDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOfficerDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.officerDashboard = action.payload;
      })
      .addCase(fetchOfficerDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Coordinator Dashboard (Phase-2)
    builder
      .addCase(fetchCoordinatorDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoordinatorDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.coordinatorDashboard = action.payload;
      })
      .addCase(fetchCoordinatorDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearDashboardError,
  clearAdminDashboard,
  clearDoctorDashboard,
  clearOfficerDashboard,
  clearCoordinatorDashboard,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;